import { Message, Conversation, PromptCategory } from '../types';
import { getCurrentUserId } from './auth';

const defaultHeaders = {
  'Content-Type': 'application/json',
  'Accept': 'application/json'
};

interface CompletionOptions {
  messages: Message[];
  apiUrl: string;
  chunkIntervalMs?: number;
  token?: string;
  userId?: string;
}

interface ApiOptions {
  token?: string;
  userId: string;
}

async function makeAuthenticatedRequest(baseUrl: string, path: string, options: RequestInit & ApiOptions) {
  const { token, userId, ...requestOptions } = options;
  const fullUrl = `${baseUrl}${path}`;
  
  // Don't add default headers if we're sending FormData
  const isFormData = requestOptions.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : defaultHeaders),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...requestOptions.headers,
  };

  const response = await fetch(fullUrl, {
    ...requestOptions,
    headers,
    credentials: 'include',
  });

  return response;
}

export const getCompletion = async function* (options: CompletionOptions) {
  try {
    console.log('Starting chat completion request');

    // Get the user ID for filtering content
    const userId = options.userId || 'anonymous';
    
    // Log validation of userId
    if (!options.userId) {
      console.warn('No userId provided for content filtering, using default:', userId);
    } else {
      console.log('Using userId for content filtering:', userId);
    }

    const response = await makeAuthenticatedRequest(
      options.apiUrl,
      '/api/chat/stream',
      {
        method: 'POST',
        body: JSON.stringify({ 
          messages: options.messages,
          userId: userId
        }),
        token: options.token,
        userId: userId
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
    }

    if (!response.body) {
      throw new Error('No response body available');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          console.log('Stream complete');
          break;
        }

        // Decode the chunk and add it to our buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete lines (NDJSON format)
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.trim() === '') continue;

          try {
            const parsed = JSON.parse(line);
            console.log('Parsed chunk:', parsed);

            if (options.chunkIntervalMs) {
              await new Promise(resolve => setTimeout(resolve, options.chunkIntervalMs));
            }

            yield parsed;
          } catch (e) {
            console.error('Error parsing line:', e, 'Line:', line);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    console.error('Error in getCompletion:', error);
    throw error;
  }
};

export const uploadDocument = async (file: File, options: ApiOptions & { apiUrl: string }) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    // Include userId in the form data
    if (options.userId) {
      formData.append('userId', options.userId);
    }

    const response = await makeAuthenticatedRequest(
      options.apiUrl,
      '/api/documents',
      {
        method: 'POST',
        body: formData,
        headers: {}, // Override default headers for multipart/form-data
        ...options
      }
    );
    return await response.json();
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

export const downloadDocument = async (filename: string, options: ApiOptions & { apiUrl: string }) => {
  try {
    const response = await makeAuthenticatedRequest(
      options.apiUrl,
      `/api/documents/${filename}`,
      {
        method: 'GET',
        ...options
      }
    );
    return await response.blob();
  } catch (error) {
    console.error('Error downloading document:', error);
    throw error;
  }
};

export const saveConversation = async (conversation: Conversation, options: ApiOptions & { apiUrl: string }) => {
  try {
    const response = await makeAuthenticatedRequest(
      options.apiUrl,
      `/api/conversations/${options.userId}`,
      {
        method: 'POST',
        body: JSON.stringify(conversation),
        ...options
      }
    );
    return await response.json();
  } catch (error) {
    console.error('Error saving conversation:', error);
    throw error;
  }
};

export const loadConversation = async (id: string, options: ApiOptions & { apiUrl: string }) => {
  try {
    const response = await makeAuthenticatedRequest(
      options.apiUrl,
      `/api/conversations/${options.userId}/${id}`,
      {
        method: 'GET',
        ...options
      }
    );
    return await response.json();
  } catch (error) {
    console.error('Error loading conversation:', error);
    throw error;
  }
};

export const loadChatHistory = async (options: ApiOptions & { apiUrl: string }) => {
  try {
    const response = await makeAuthenticatedRequest(
      options.apiUrl,
      `/api/conversations/${options.userId}`,
      {
        method: 'GET',
        ...options
      }
    );
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error loading chat history:', error);
    throw error;
  }
};

export const deleteChatHistory = async (options: ApiOptions & { apiUrl: string }) => {
  try {
    const response = await makeAuthenticatedRequest(
      options.apiUrl,
      `/api/conversations/${options.userId}`,
      {
        method: 'DELETE',
        ...options
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting chat history:', error);
    throw error;
  }
};

export const getPrompts = async (options: ApiOptions & { apiUrl: string }): Promise<PromptCategory[]> => {
  try {
    const response = await makeAuthenticatedRequest(
      options.apiUrl,
      '/api/prompts',
      {
        method: 'GET',
        ...options
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch prompts: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching prompts:', error);
    throw error;
  }
};

export const getPromptsByCategory = async (category: string, options: ApiOptions & { apiUrl: string }): Promise<PromptCategory[]> => {
  try {
    const response = await makeAuthenticatedRequest(
      options.apiUrl,
      `/api/prompts/${category}`,
      {
        method: 'GET',
        ...options
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch prompts for category ${category}: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching prompts for category ${category}:`, error);
    throw error;
  }
}; 